import { createColumnHelper } from "@tanstack/react-table";
import { Checkbox } from "@tonic/shared/components/ui";
import { Badge } from "@tonic/shared/components/ui";
import { Button } from "@tonic/shared/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@tonic/shared/components/ui";
import { StatusBadge } from "@/features/shared/components/data-display/StatusBadge";
import { MoreHorizontal } from "lucide-react";
import { InventoryItem, InventoryStatus } from "../../types";

/**
 * Get the CSS color class for a given inventory status
 * @param {import("../../types").InventoryStatus} status - The inventory status
 * @returns {string} The CSS color class
 */
const getStatusColor = (status) => {
  /** @type {Record<import("../../types").InventoryStatus, string>} */
  const colors = {
    new: "bg-blue-500",
    in_recommerce: "bg-yellow-500",
    repaired: "bg-green-500",
    awaiting_refill: "bg-orange-500",
    out_of_stock: "bg-red-500",
    scrapped: "bg-gray-500",
    on_rental: "bg-purple-500",
  };
  return colors[status] || "bg-gray-500";
};

const columnHelper = createColumnHelper();

/** @type {ReturnType<typeof columnHelper.accessor>[]} */
export const columns = [
  columnHelper.display({
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onChange={(e) => row.toggleSelected(e.target.checked)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  }),
  columnHelper.accessor("id", {
    header: "ID",
    cell: ({ row }) => <span className="font-mono text-xs">{row.getValue("id")}</span>
  }),
  columnHelper.accessor("sku", {
    header: "SKU",
  }),
  columnHelper.accessor("name", {
    header: "Name",
  }),
  columnHelper.accessor("category", {
    header: "Category",
  }),
  columnHelper.accessor("status", {
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status");
      return <StatusBadge status={status} />;
    },
  }),
  columnHelper.accessor("location", {
    header: "Location",
  }),
  columnHelper.accessor("quantity", {
    header: "Quantity",
  }),
  columnHelper.display({
    id: "actions",
    cell: ({ row }) => {
      const item = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => navigator.clipboard.writeText(item.id)}>
              Copy ID
            </DropdownMenuItem>
            <DropdownMenuItem>View Details</DropdownMenuItem>
            <DropdownMenuItem>Edit Item</DropdownMenuItem>
            <DropdownMenuItem>Update Status</DropdownMenuItem>
            <DropdownMenuItem className="text-red-600">Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  }),
]; 